import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import './CommonStyles.css';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import dateUtils from '../../utils/dateUtils';

const emptyRow = (remainingDue) => ({
    method: 'cash',
    paidOn: dateUtils.getChicagoISOString(),
    amount: remainingDue > 0 ? remainingDue.toFixed(2) : '',
    accountId: '',
    note: '',
    cardNumber: '', cardHolderName: '', cardType: 'visa', cardMonth: '', cardYear: '', cardSecurity: '', cardTransactionNumber: '',
    chequeNumber: '', chequeExpiryDate: '', bankSubmissionDate: '',
    bankAccountNumber: '',
});

const AddPaymentModal = ({ sale, invoice, onClose, onSuccess }) => {
    const [rows, setRows] = useState([]);
    const [paymentAccounts, setPaymentAccounts] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    

    // Extract sale/invoice data
    const transactionId = sale?.id || sale?.apiData?.id;
    const customerName = sale?.customer || sale?.contact?.name || '--';
    const invoiceNo = sale?.invoice_no || sale?.ref_no || '--';
    const locationName = sale?.location?.name || '--';
    const locationId = sale?.location?.id || sale?.location_id || null;
    const finalTotal = parseFloat(sale?.final_total || sale?.total_payable || 0);
    const paymentDue = parseFloat(sale?.payment_due ?? finalTotal);

    const loadPaymentMethods = async (location_id = null) => {
        try {
            const paymentMethodsData = await apiService.getPaymentMethods(location_id);
            let transformed = [];
            if (typeof paymentMethodsData === 'object' && paymentMethodsData !== null && !Array.isArray(paymentMethodsData)) {
                transformed = Object.entries(paymentMethodsData).map(([key, value]) => {
                    let methodKey = key;
                    if (methodKey === 'check') methodKey = 'cheque';
                    return {
                        id: methodKey,
                        name: typeof value === 'string' ? value : (value.name || key),
                        type: methodKey
                    };
                });
            } else if (Array.isArray(paymentMethodsData?.data)) {
                transformed = paymentMethodsData.data.map(m => {
                    let key = m.id || m.key || m.name || 'cash';
                    if (key === 'check') key = 'cheque';
                    return { id: key, name: m.name || m.label || key, type: key };
                });
            }
            if (transformed.length === 0) {
                transformed = [
                    { id: 'cash', name: 'Cash', type: 'cash' },
                    { id: 'card', name: 'Card', type: 'card' },
                    { id: 'cheque', name: 'Check', type: 'cheque' },
                    { id: 'bank_transfer', name: 'Bank Transfer', type: 'bank_transfer' },
                    { id: 'credit', name: 'Credit', type: 'credit' },
                    { id: 'other', name: 'Other', type: 'other' }
                ];
            }
            setPaymentMethods(transformed);
        } catch (error) {
            setPaymentMethods([
                { id: 'cash', name: 'Cash', type: 'cash' },
                { id: 'card', name: 'Card', type: 'card' },
                { id: 'cheque', name: 'Check', type: 'cheque' },
                { id: 'bank_transfer', name: 'Bank Transfer', type: 'bank_transfer' },
                { id: 'credit', name: 'Credit', type: 'credit' },
                { id: 'other', name: 'Other', type: 'other' }
            ]);
        }
    };

    // Load payment accounts + payment methods + initialise first row with remaining due
    useEffect(() => {
        const params = locationId ? { location_id: locationId, per_page: -1 } : { per_page: -1 };
        apiService.getPaymentAccounts(params)
            .then(res => setPaymentAccounts(res.data || res || []))
            .catch(() => setPaymentAccounts([]));
        loadPaymentMethods(locationId);
        setRows([emptyRow(paymentDue)]);
    }, [locationId]);

    const updateRow = (idx, field, value) => {
        setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    };

    const addRow = () => {
        const alreadyEntered = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        const remaining = Math.max(0, paymentDue - alreadyEntered);
        setRows(prev => [...prev, emptyRow(remaining)]);
    };

    const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));

    const formatDT = (str) => str ? str.replace('T', ' ') + ':00' : null;

    const handleSave = async () => {
        setError('');
        if (!transactionId) { setError('Transaction ID is missing'); return; }
        if (rows.length === 0) { setError('Add at least one payment row'); return; }
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (!r.amount || parseFloat(r.amount) <= 0) {
                setError(`Row ${i + 1}: enter a valid amount`); return;
            }
            if (r.method === 'card' && (!r.cardNumber || !r.cardHolderName)) {
                setError(`Row ${i + 1}: card number and holder name are required`); return;
            }
            if (r.method === 'bank_transfer' && !r.bankAccountNumber) {
                setError(`Row ${i + 1}: bank account number is required`); return;
            }
        }
        try {
            setLoading(true);
            for (const r of rows) {
                let methodKey = (r.method || 'cash').toLowerCase();
                if (methodKey === 'check') methodKey = 'cheque';
                const payload = {
                    transaction_id: parseInt(transactionId),
                    method: methodKey,
                    amount: parseFloat(r.amount),
                    paid_on: formatDT(r.paidOn),
                    payment_account_id: r.accountId || null,
                    payment_note: r.note || null,
                };
                if (r.method === 'card') {
                    payload.card_number = r.cardNumber;
                    payload.card_holder_name = r.cardHolderName;
                    payload.card_type = r.cardType;
                    if (r.cardMonth) payload.card_month = r.cardMonth;
                    if (r.cardYear) payload.card_year = r.cardYear;
                    if (r.cardSecurity) payload.card_security = r.cardSecurity;
                    if (r.cardTransactionNumber) payload.card_transaction_number = r.cardTransactionNumber;
                }
                if (r.method === 'cheque') {
                    if (r.chequeNumber) payload.cheque_number = r.chequeNumber;
                    if (r.chequeExpiryDate) payload.cheque_expiry_date = r.chequeExpiryDate;
                    if (r.bankSubmissionDate) payload.bank_submission_date = r.bankSubmissionDate;
                }
                if (r.method === 'bank_transfer') {
                    payload.bank_account_number = r.bankAccountNumber;
                }
                await apiService.addPayment(payload);
            }
            setLoading(false);
            toast.success('Payment saved successfully!');
            setTimeout(() => { if (onSuccess) onSuccess(); onClose(); }, 1200);
        } catch (err) {
            setError(err.message || 'Failed to save payments');
            setLoading(false);
        }
    };

    const inp = (style = {}) => ({ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: 'var(--etpos-text)', width: '100%', background: 'var(--etpos-card)', ...style });
    const sel = (style = {}) => ({ ...inp(), background: 'var(--etpos-card)', ...style });

    return (
        <>
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem' }}>
            <div style={{ background: 'var(--etpos-card)', width: '100%', maxWidth: '960px', borderRadius: '12px', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)', maxHeight: '92vh', overflow: 'hidden' }}>

                {/* Header */}
                <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700' }}>Add Payment</h3>
                    <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer', fontSize: '1.25rem' }}>✕</button>
                </div>

                {/* Body */}
                <div style={{ padding: '1.25rem', overflowY: 'auto', flex: 1 }}>

                    {error && (
                        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '0.65rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' }}>
                            {error}
                        </div>
                    )}

                    {/* Info Cards */}
                    <div className="payment-info-cards" style={{ marginBottom: '1rem' }}>
                        <div className="info-card">
                            <label>Customer / Supplier :</label>
                            <span>{customerName}</span>
                        </div>
                        <div className="info-card">
                            <label>Invoice No.:</label>
                            <span>{invoiceNo}</span>
                            <label>Location:</label>
                            <span>{locationName}</span>
                        </div>
                        <div className="info-card">
                            <label>Total amount:</label>
                            <span>$ {finalTotal.toFixed(2)}</span>
                            <label>Payment Note:</label>
                            <span>--</span>
                        </div>
                    </div>

                    {/* Advance Balance */}
                    <div className="advance-balance" style={{ marginBottom: '1rem' }}>
                        <span>Advance Balance: $0.00</span>
                    </div>

                    {/* Payment rows */}
                    {rows.map((row, idx) => (
                        <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '1rem', marginBottom: '1rem', background: 'var(--etpos-bg, #f8fafc)', position: 'relative' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Payment #{idx + 1}</span>
                                {rows.length > 1 && (
                                    <button onClick={() => removeRow(idx)} style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer', padding: '0.2rem' }}>
                                        <Trash2 size={15} />
                                    </button>
                                )}
                            </div>

                            {/* Row 1: Method | Paid On | Amount */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                                <div>
                                    <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Payment Method:*</label>
                                    <select style={sel()} value={row.method} onChange={e => updateRow(idx, 'method', e.target.value)} disabled={loading}>
                                        {paymentMethods.length > 0 ? paymentMethods.map(m => (
                                            <option key={m.id} value={m.id}>{m.name}</option>
                                        )) : (
                                            <>
                                                <option value="cash">Cash</option>
                                                <option value="card">Card</option>
                                                <option value="cheque">Check</option>
                                                <option value="bank_transfer">Bank Transfer</option>
                                                <option value="credit">Credit</option>
                                                <option value="other">Other</option>
                                            </>
                                        )}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Paid On:*</label>
                                    <input type="datetime-local" style={inp()} value={row.paidOn} onChange={e => updateRow(idx, 'paidOn', e.target.value)} disabled={loading} />
                                </div>
                                <div>
                                    <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Amount:*</label>
                                    <div className="amount-input-wrapper" style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: '4px', background: 'var(--etpos-card)' }}>
                                        <span className="currency-icon" style={{ padding: '0 8px', fontSize: '1rem' }}>💵</span>
                                        <input type="number" step="0.01" min="0" style={{ border: 'none', outline: 'none', padding: '0.45rem 0.6rem', width: '100%', fontSize: '0.82rem', background: 'transparent', color: 'var(--etpos-text)' }} value={row.amount} onChange={e => updateRow(idx, 'amount', e.target.value)} disabled={loading} />
                                    </div>
                                </div>
                            </div>

                            {/* Row 2: Account | Note */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: row.method === 'card' || row.method === 'cheque' || row.method === 'bank_transfer' ? '0.75rem' : 0 }}>
                                <div>
                                    <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Payment Account:</label>
                                    <select style={sel()} value={row.accountId} onChange={e => updateRow(idx, 'accountId', e.target.value)} disabled={loading}>
                                        <option value="">None</option>
                                        {paymentAccounts.map(acc => (
                                            <option key={acc.id} value={acc.id}>
                                                {acc.name || acc.account_name || acc.display_name || `Account #${acc.id}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Payment Note:</label>
                                    <input type="text" style={inp()} value={row.note} onChange={e => updateRow(idx, 'note', e.target.value)} placeholder="Optional note" disabled={loading} />
                                </div>
                            </div>

                            {/* Card extra fields */}
                            {row.method === 'card' && (
                                <div style={{ background: 'var(--etpos-border)', borderRadius: '4px', padding: '0.75rem', marginTop: '0.75rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                        <div>
                                            <label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Card Number:*</label>
                                            <input type="text" style={inp()} value={row.cardNumber} onChange={e => updateRow(idx, 'cardNumber', e.target.value)} placeholder="1234 5678 9012 3456" />
                                        </div>
                                        <div>
                                            <label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Card Holder:*</label>
                                            <input type="text" style={inp()} value={row.cardHolderName} onChange={e => updateRow(idx, 'cardHolderName', e.target.value)} placeholder="John Doe" />
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '0.5rem' }}>
                                        <div>
                                            <label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Type</label>
                                            <select style={sel()} value={row.cardType} onChange={e => updateRow(idx, 'cardType', e.target.value)}>
                                                <option value="visa">Visa</option>
                                                <option value="mastercard">Mastercard</option>
                                                <option value="amex">Amex</option>
                                                <option value="discover">Discover</option>
                                            </select>
                                        </div>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Month</label><input type="text" maxLength="2" style={inp()} value={row.cardMonth} onChange={e => updateRow(idx, 'cardMonth', e.target.value)} placeholder="MM" /></div>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Year</label><input type="text" maxLength="4" style={inp()} value={row.cardYear} onChange={e => updateRow(idx, 'cardYear', e.target.value)} placeholder="YYYY" /></div>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>CVV</label><input type="text" maxLength="4" style={inp()} value={row.cardSecurity} onChange={e => updateRow(idx, 'cardSecurity', e.target.value)} placeholder="123" /></div>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Txn #</label><input type="text" style={inp()} value={row.cardTransactionNumber} onChange={e => updateRow(idx, 'cardTransactionNumber', e.target.value)} /></div>
                                    </div>
                                </div>
                            )}

                            {/* Cheque extra fields */}
                            {(row.method === 'cheque' || row.method === 'check' || row.method === 'Cheque' || row.method === 'Check') && (
                                <div style={{ background: 'var(--etpos-border)', borderRadius: '4px', padding: '0.75rem', marginTop: '0.75rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Cheque Number</label><input type="text" style={inp()} value={row.chequeNumber} onChange={e => updateRow(idx, 'chequeNumber', e.target.value)} /></div>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Cheque Expiry</label><input type="date" style={inp()} value={row.chequeExpiryDate} onChange={e => updateRow(idx, 'chequeExpiryDate', e.target.value)} /></div>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Bank Submission Date</label><input type="date" style={inp()} value={row.bankSubmissionDate} onChange={e => updateRow(idx, 'bankSubmissionDate', e.target.value)} /></div>
                                    </div>
                                </div>
                            )}

                            {/* Bank Transfer extra fields */}
                            {row.method === 'bank_transfer' && (
                                <div style={{ background: 'var(--etpos-border)', borderRadius: '4px', padding: '0.75rem', marginTop: '0.75rem' }}>
                                    <label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Bank Account Number:*</label>
                                    <input type="text" style={inp({ maxWidth: '320px' })} value={row.bankAccountNumber} onChange={e => updateRow(idx, 'bankAccountNumber', e.target.value)} placeholder="Enter bank account number" />
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Add Payment Row button */}
                    <button
                        onClick={addRow}
                        disabled={loading}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.45rem 1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginBottom: '0.5rem' }}
                    >
                        <Plus size={15} /> Add Payment
                    </button>

                </div>

                {/* Footer */}
                <div style={{ padding: '1rem 1.25rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'center', gap: '0.75rem', background: 'var(--etpos-card, #fff)' }}>
                    <button onClick={onClose} disabled={loading} style={{ background: '#fff', border: '1px solid #cbd5e1', color: '#374151', borderRadius: '6px', padding: '0.55rem 1.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', minWidth: '100px' }}>
                        Close
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        style={{ background: loading ? '#9ca3af' : '#10b981', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '0.55rem 1.6rem', fontSize: '0.875rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', minWidth: '135px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    >
                        {loading ? '⏳ Saving...' : '💸 Add Payment'}
                    </button>
                </div>

            </div>
        </div>
        </>
    );
};

export default AddPaymentModal;
